import React, { Fragment } from 'react';

import { FormRow } from 'src/components/Form';
import { InfoOverlayPropTypes } from 'src/components/JobDatasheet/propTypes';
import { ReportReviewBlock } from 'src/components/ReportReviewBlock';
import { ResultHistoriesBlock } from 'src/components/ResultHistoriesBlock';
import { useTranslation } from 'react-i18next';
import { DELETE_ATTACHMENT_MUTATION } from 'src/graphql/attachment/mutation';
import { useMutation } from 'src/graphql';
import Attachments from 'src/components/Attachments';
import s from 'src/components/JobDatasheet/JobDatasheetForm/JobDatasheetForm.styles';
import TextField from 'src/components/TextField';
import { withJobContext } from 'src/utils/context/JobContext';

const InfoOverlayComponent = ({
  jobContext,
  activeQuestion,
  activeReportReviewComments,
  activeReportReviewNote,
  addNote,
  datasheetCurrentVersionId,
  datasheetId,
  getResult,
  getResultAttachments,
  getResultExternalAttachments,
  forExternalUsersOnly,
  hasSaqService,
  hasUnverifiedSaqService,
  isCreateNote,
  isInfoCollapsed,
  isSidebarExpanded,
  isUnderReviewing,
  jobId,
  onDeletingExternalAttachment,
  readonly,
  refetchReportReview,
  resultHistories,
  setIsCreateNote,
  setIsInfoCollapsed,
  setResult,
  setShowNote,
  setShowResult,
  showHistory,
  showNote,
  showResult,
  userIsAuthorizedExternalSaqUser
}) => {
  const { t } = useTranslation();

  const {
    job,
    user: {
      isAuditor,
      isReportReviewer,
      isInternalUser,
    },
    permissions: {
      isReportNotesAvailableToAuditor,
    },
  } = jobContext;

  const activeQuestionCurrentVersion = activeQuestion?.datasheetQuestion?.currentVersion;
  const descriptionToShow = isInternalUser ? activeQuestionCurrentVersion?.descriptionAlias : activeQuestionCurrentVersion?.descriptionExternal;

  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT_MUTATION);

  return (
    <s.InfoOverlay hidden={isInfoCollapsed} isSidebarExpanded={isSidebarExpanded}>
      <s.InfoOverlayContent>
        {activeQuestion && (
          <s.AddNoteButtonWithTitleDiv>
            <s.CloseBtn name="cross" onClick={() => setIsInfoCollapsed(true)} />
            <s.QuestionTitle>
              {t('DATASHEET_QUESTION_DETAILS')}
            </s.QuestionTitle>

            {((isUnderReviewing &&
              isReportReviewer) || (isReportNotesAvailableToAuditor && isAuditor)) &&
              !isCreateNote &&
              !activeReportReviewNote &&
              !forExternalUsersOnly && (
                <s.AddButtonWrapper onClick={addNote}>
                  <s.AddButton name="plus" />
                  <s.AddButtonLabel>{t('ADD_NOTE')}</s.AddButtonLabel>
                </s.AddButtonWrapper>
              )}
          </s.AddNoteButtonWithTitleDiv>
        )}
        {forExternalUsersOnly && activeQuestion &&
          <>
            <s.QuestionText>
              {activeQuestionCurrentVersion?.text}
            </s.QuestionText>
          </>
        }
        {<ReportReviewBlock
          activeReportReviewComments={activeReportReviewComments}
          activeReportReviewNote={activeReportReviewNote}
          isCreateNote={isCreateNote}
          isUnderReviewing={isUnderReviewing}
          jobId={jobId}
          readonly={readonly}
          refetchReportReview={refetchReportReview}
          setIsCreateNote={setIsCreateNote}
          setShowNote={setShowNote}
          setShowResult={setShowResult}
          showNote={showNote}
          type="datasheet"
          typeId={datasheetId}
          typeQuestionId={activeQuestion && activeQuestion.id}
          typeVersionId={datasheetCurrentVersionId}
          userIsAuthorizedExternalSaqUser={userIsAuthorizedExternalSaqUser}
        />}
        {!isCreateNote && showHistory && (
          <ResultHistoriesBlock resultHistories={resultHistories} />
        )}
        {!forExternalUsersOnly && activeQuestion && !isCreateNote && showResult && (
          <Fragment>
            <s.QuestionText>
              {activeQuestionCurrentVersion?.text}
            </s.QuestionText>
            {descriptionToShow && (
              <s.QuestionDescription>
                {descriptionToShow}
              </s.QuestionDescription>
            )}

                <s.BlockLabel>{t('COMMENTS')}</s.BlockLabel>
                <FormRow>
                  <TextField
                    fullsize
                    multiline
                    onChange={e =>
                      setResult(activeQuestion, 'comments', e.target.value)
                    }
                    value={getResult(activeQuestion).comments || ''}
                  />
                </FormRow>


            <Fragment>
              <s.BlockLabel>{t('ATTACHMENTS')}</s.BlockLabel>
              <Attachments
                key={JSON.stringify(activeQuestion)}
                attachmentIds={getResultAttachments(activeQuestion)}
                disabled={readonly}
                entity="attachments"
                onChange={(_filename, _entity, id, meta) => {
                  setResult(
                    activeQuestion,
                    'attachments',
                    (getResult(activeQuestion).attachments || []).concat({
                      attachmentId: id,
                    }),
                  );
                  setResult(
                    activeQuestion,
                    'attachmentsMeta',
                    (getResult(activeQuestion).attachmentsMeta || []).concat(
                      meta,
                    ),
                  );
                }}
                onDeleteFile={async (filename, attId) => {
                  if (filename !== 'loading.file' && attId) {
                    setResult(
                      activeQuestion,
                      'attachments',
                      (getResult(activeQuestion).attachments || [])
                        .filter(({ attachmentId }) => String(attachmentId) !== String(attId)));

                    setResult(
                      activeQuestion,
                      'attachmentsMeta',
                      (getResult(activeQuestion).attachmentsMeta || [])
                        .filter(({ attId: attachmentId }) => String(attachmentId) !== String(attId)));

                    await deleteAttachment({
                      variables: {
                        id: attId,
                      },
                    });
                  }
                }}
                pathPrefix={`job_${jobId}`}
              />
            </Fragment>
          </Fragment>
        )}
        {(hasSaqService || hasUnverifiedSaqService) && activeQuestion && !showNote && <Fragment>
          <s.BlockLabel>{t('EXTERNAL_ATTACHMENTS')}</s.BlockLabel>
          <Attachments
            key={JSON.stringify(activeQuestion)}
            allowRemove={!!readonly}
            attachmentIds={getResultExternalAttachments(activeQuestion)}
            disabled={!!readonly}
            entity="attachments"
            labelFileProcessingComplete=""
            labelTapToUndo=""
            onChange={(_filename, _entity, id, meta) => {
              setResult(
                activeQuestion,
                'externalAttachments',
                (getResult(activeQuestion).externalAttachments || []).concat({
                  attachmentId: id,
                }),
              );
              setResult(
                activeQuestion,
                'externalAttachmentsMeta',
                (getResult(activeQuestion).externalAttachmentsMeta || []).concat(
                  meta,
                ),
              );
            }}
            onDeleteFile={readonly ? null: onDeletingExternalAttachment}
            pathPrefix={`job_${jobId}`}
          />
        </Fragment>}
      </s.InfoOverlayContent>
    </s.InfoOverlay>
  );
};

InfoOverlayComponent.propTypes = InfoOverlayPropTypes;

export const InfoOverlay = React.memo(withJobContext(InfoOverlayComponent));
